import React, { createContext, useContext, useState, useCallback } from 'react';
import { Params } from '../types/config';
import { FeedInCalculationParams } from '../types/calculations';
import { calculateFeedInEffectiveRate } from '../utils/energyCalculations';

/**
 * Default system parameters
 * 
 * These values represent the initial state of all financial, technical, and operational
 * parameters used throughout the application. They serve as the starting point for user
 * customization in the dashboard.
 */
const defaultParams: Params = {
  // System costs
  solarCost: 3000000,          // Total solar PV system cost in AED
  batteryCost: 7000000,        // Total battery storage system cost in AED
  connectionCost: 600000,      // Grid connection cost in AED
  maintenanceCost: 1500000,    // Cost per maintenance intervention in AED (capitalized as major overhaul)
  batteryResidualValue: 0.25,  // 25% residual value for second-life applications
  
  // Operational costs
  gridRate: 0.44,              // Grid electricity price in AED per kWh
  feedInRate: 0.105,           // Nominal feed-in tariff in AED per kWh (10.5 fils)
  feedInEffectiveRate: 0.071,  // Effective feed-in rate after limitations in AED per kWh
  generatorFuelCost: 2.3545,   // Diesel fuel cost in AED per liter
  generatorConsumption: 85,    // Generator fuel consumption in liters per hour
  
  // Battery parameters
  batteryCycles: 6000,                // Total battery lifecycle in charge/discharge cycles
  batteryMaintenanceYears: [10, 20],  // Years when battery maintenance is performed
  batteryEfficiency: 0.9,             // 90% round-trip battery efficiency
  
  // Analysis parameters
  solarAnalysisYears: 30,      // Extended analysis period for solar components in years
  batteryAnalysisYears: 25,    // Standard analysis period for battery components in years
  discountRate: 0.035,         // 3.5% discount rate for NPV calculations (reduced from 5% to reflect low-risk predictable cash flows)
  gridRateEscalation: 0.0,     // No annual escalation in grid electricity rates
  
  // Grid services & additional value streams
  frequencyRegulationRevenue: 25000,  // Annual revenue from frequency regulation services in AED
  demandResponseRevenue: 15000,       // Annual revenue from demand response services in AED
  
  // Feature toggles
  showGridTiedAnalysis: false,  // When set to false, hides all Grid-Tied Solar analysis options from the dashboard
};

/**
 * Example monthly solar exports data
 * 
 * Represents the monthly pattern of excess solar energy exported to the grid,
 * used to calculate the effective feed-in rate with seasonal limitations.
 */
const monthlyExportsExample = [
  10000, // January - winter month with export limitations
  9000,  // February - winter month with export limitations
  12000, // March
  15000, // April
  20000, // May
  22000, // June
  25000, // July
  24000, // August
  21000, // September
  18000, // October
  12000, // November
  11000  // December - winter month with export limitations
];

/**
 * ParamsContext type definition
 * 
 * Defines the shape of the context object that will be provided to consumers,
 * including the current parameters state and methods to update it.
 */
interface ParamsContextType {
  params: Params;                                             // Current parameters state
  updateParam: (name: any, value: number | number[]) => void;  // Method to update a single parameter
  updateFeedInRate: (value: number) => void;                  // Special method for feed-in rate updates
}

/**
 * Create ParamsContext with default values
 * 
 * The default values are used when a component consumes the context without
 * being wrapped in a ParamsProvider.
 */
export const ParamsContext = createContext<ParamsContextType>({
  params: defaultParams,
  updateParam: () => {},
  updateFeedInRate: () => {}
});

/**
 * ParamsProvider component
 * 
 * This component manages the global state for all system parameters used in financial,
 * energy, and environmental calculations. It provides methods to update individual parameters
 * and handles special cases like feed-in rate updates that require recalculation of derived values.
 * 
 * Usage:
 * ```tsx
 * <ParamsProvider>
 *   <App />
 * </ParamsProvider>
 * ```
 * 
 * @param props - Component props with children
 * @returns Provider component that makes params state available to all children
 */
export const ParamsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State for all system parameters
  const [params, setParams] = useState<Params>(defaultParams);
  
  /**
   * Update a parameter value
   * 
   * Generic method to update any parameter in the params state.
   * Simply replaces the old value with the new one without side effects.
   * 
   * @param name - The parameter key to update
   * @param value - The new parameter value
   */
  const updateParam = useCallback((
    name: keyof Params, 
    value: number | number[]
  ): void => {
    setParams(prevParams => ({ ...prevParams, [name]: value }));
  }, []);
  
  /**
   * Update feed-in rate with recalculation of effective rate
   * 
   * Special update method for the feed-in rate that also recalculates
   * the effective feed-in rate based on seasonal and regulatory limitations.
   * 
   * @param value - The new nominal feed-in rate in AED/kWh
   */
  const updateFeedInRate = useCallback((value: number): void => {
    // First update the nominal rate
    setParams(prevParams => ({ ...prevParams, feedInRate: value }));
    
    // Calculate new effective rate based on the newly set feed-in rate
    const feedInParams: FeedInCalculationParams = {
      monthlyExports: monthlyExportsExample,   // Monthly pattern of exports
      feedInRate: value,                       // New nominal rate
      winterMonths: [12, 1, 2],                // Months with winter export restrictions (Dec, Jan, Feb)
      maxLoadLimitFraction: 0.05,              // 5% of exports disallowed due to grid load limitations
      maintenanceDowntime: 0.02                // 2% of time no feed-in allowed due to maintenance
    };
    
    // Calculate the new effective rate considering all limitations
    const newEffectiveRate = calculateFeedInEffectiveRate(feedInParams);
    
    // Update the effective rate in state
    setParams(prevParams => ({ ...prevParams, feedInEffectiveRate: newEffectiveRate }));
  }, []);
  
  // Provide context value to children
  return (
    <ParamsContext.Provider value={{ 
      params, 
      updateParam,
      updateFeedInRate
    }}>
      {children}
    </ParamsContext.Provider>
  );
};

/**
 * Custom hook for using the params context
 * 
 * This hook provides convenient access to the params context in functional components.
 * It handles the context consumption and throws an error if used outside a ParamsProvider.
 * 
 * Usage:
 * ```tsx
 * const { params, updateParam, updateFeedInRate } = useParams();
 * ```
 * 
 * @returns The current params context value
 */
export const useParams = () => useContext(ParamsContext);